import {useEffect, useRef} from "react";
import {NavigationType, type createBrowserRouter} from "react-router";
import {useQueryClient} from "@tanstack/react-query";
import {useAppDispatch, useAppSelector} from "./reduxHooks.ts";
import {authReducerActions} from "../reducer/authReducer.ts";
import {RightsMapper} from "../components/rights/RightsMapper.ts";
import {saveSupportContext} from "../utils/supportContextStorage.ts";
import {markSupportContextEntry, sameSupportContext, supportContextOfEntry} from "../utils/supportContextHistory.ts";

/**
 * Browser back and forward return to the support context of the history entry
 * (#56913): going back from a context to the admin view leaves the context, going
 * forward again enters it. Every other navigation records the current context.
 */
export const useSupportContextHistory = (router: ReturnType<typeof createBrowserRouter>) => {
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const context = useAppSelector(state => state.auth.supportContext);
    const authorities = useAppSelector(state => state.auth.profile?.authorities as string[] | undefined);

    const current = useRef({context, authorities});
    useEffect(() => {
        current.current = {context, authorities};
    }, [context, authorities]);

    useEffect(() => {
        // the entry the router starts at, e.g. after entering a context
        markSupportContextEntry(current.current.context);
        return router.subscribe(state => {
            if (state.navigation.state !== "idle") return;
            const {context: active, authorities: granted} = current.current;
            if (state.historyAction === NavigationType.Pop) {
                const recorded = supportContextOfEntry();
                const allowed = recorded === null
                    || RightsMapper.getUserPermissions(granted ?? []).includes("SupportContext_write");
                if (recorded !== undefined && allowed && !sameSupportContext(recorded, active)) {
                    // switching rebuilds the router, which then shows this entry in its own view
                    saveSupportContext(recorded);
                    queryClient.clear();
                    dispatch(recorded ? authReducerActions.startSupportContext(recorded) : authReducerActions.endSupportContext());
                    return;
                }
            }
            markSupportContextEntry(active);
        });
    }, [router, dispatch, queryClient]);
};
